import type { Disposable } from "@/shared/contracts/lifecycle";
import type { HoverPointerModel } from "./hoverPointerModel";

export const NO_VIRTUAL_POINTER_SELECTORS: readonly string[] = [
  "button",
  "input",
  "textarea",
  "select",
  "a[href]",
  "[data-no-virtual-pointer]",
];

export const VIRTUAL_POINTER_ALLOW_SELECTOR = '[data-virtual-pointer="allow"]';
export const HOVER_ANCHOR_TARGET_ID_ATTRIBUTE = "data-hover-anchor-target-id";
export const HOVER_ANCHOR_TARGET_ID_SELECTOR = `[${HOVER_ANCHOR_TARGET_ID_ATTRIBUTE}]`;

export interface ClosestQueryable {
  closest(selectors: string): ClosestQueryable | null;
  getAttribute?(qualifiedName: string): string | null;
}

export interface HoverPointerEventLike {
  readonly pointerType: string;
  readonly clientX: number;
  readonly clientY: number;
  readonly target: ClosestQueryable | null;
  readonly hoverAnchorTargetId?: string | null;
}

interface ApplyPointerDownToModelOptions {
  readonly shouldTrackVirtualPointer?: boolean;
}

export function shouldSkipVirtualPointer(
  target: ClosestQueryable | null,
): boolean {
  if (target === null) {
    return false;
  }
  if (target.closest(VIRTUAL_POINTER_ALLOW_SELECTOR) !== null) {
    return false;
  }
  for (const selector of NO_VIRTUAL_POINTER_SELECTORS) {
    if (target.closest(selector) !== null) {
      return true;
    }
  }
  return false;
}

function allowsVirtualPointer(target: ClosestQueryable | null): boolean {
  return (
    target !== null && target.closest(VIRTUAL_POINTER_ALLOW_SELECTOR) !== null
  );
}

export function applyPointerMoveToModel(
  event: HoverPointerEventLike,
  model: HoverPointerModel,
): void {
  if (event.pointerType !== "mouse") {
    return;
  }
  model.setMousePosition({ x: event.clientX, y: event.clientY });
}

export function applyPointerDownToModel(
  event: HoverPointerEventLike,
  model: HoverPointerModel,
  options: ApplyPointerDownToModelOptions = {},
): void {
  if (event.pointerType === "mouse") {
    return;
  }
  if (
    options.shouldTrackVirtualPointer === false &&
    !allowsVirtualPointer(event.target)
  ) {
    model.clearVirtualTouchPosition();
    return;
  }
  if (shouldSkipVirtualPointer(event.target)) {
    return;
  }
  model.setVirtualTouchPosition(
    { x: event.clientX, y: event.clientY },
    { hoverAnchorTargetId: event.hoverAnchorTargetId ?? null },
  );
}

function toEventLike(event: PointerEvent): HoverPointerEventLike {
  const target = event.target;
  const closestQueryable =
    target !== null && typeof (target as Element).closest === "function"
      ? (target as unknown as ClosestQueryable)
      : null;
  const hoverAnchorTarget =
    closestQueryable?.closest(HOVER_ANCHOR_TARGET_ID_SELECTOR) ?? null;
  return {
    pointerType: event.pointerType,
    clientX: event.clientX,
    clientY: event.clientY,
    target: closestQueryable,
    hoverAnchorTargetId:
      hoverAnchorTarget?.getAttribute?.(HOVER_ANCHOR_TARGET_ID_ATTRIBUTE) ??
      null,
  };
}

interface HoverPointerInputControllerOptions {
  readonly model: HoverPointerModel;
  readonly shouldTrackVirtualPointer?: () => boolean;
  readonly target?: Document | HTMLElement;
}

export class HoverPointerInputController implements Disposable {
  private readonly model: HoverPointerModel;
  private readonly shouldTrackVirtualPointer: (() => boolean) | null;
  private readonly target: Document | HTMLElement;

  private readonly handlePointerMove = (event: Event): void => {
    applyPointerMoveToModel(toEventLike(event as PointerEvent), this.model);
  };

  private readonly handlePointerDown = (event: Event): void => {
    applyPointerDownToModel(toEventLike(event as PointerEvent), this.model, {
      shouldTrackVirtualPointer: this.shouldTrackVirtualPointer?.() ?? true,
    });
  };

  constructor(options: HoverPointerInputControllerOptions) {
    this.model = options.model;
    this.shouldTrackVirtualPointer = options.shouldTrackVirtualPointer ?? null;
    this.target = options.target ?? document;
    this.target.addEventListener("pointermove", this.handlePointerMove, true);
    this.target.addEventListener("pointerdown", this.handlePointerDown, true);
  }

  dispose(): void {
    this.target.removeEventListener(
      "pointermove",
      this.handlePointerMove,
      true,
    );
    this.target.removeEventListener(
      "pointerdown",
      this.handlePointerDown,
      true,
    );
  }
}
